import { IconCopy } from '@tabler/icons-react';
import { Pill } from '@zorro/design-system/v2/Pill';
import { CardChipIcon, LogoDark, VisaLogo } from '@zorro/shared/assets';
import { Box, Group, Stack, Text } from '@zorro/web/design-system';
import { showSuccessNotification } from '@zorro/web/ui-utils';

import { CopyToClipboard } from '../../CopyToClipboard';
import {
  getFormattedCVV,
  getFormattedCardNumber,
} from '../paymentsPciDssMasking.utils';
import classes from './CreditCard.module.scss';

const DEFAULT_MAX_WIDTH = 420; // 26.25rem, matches CreditCard.module.scss

type Props = {
  cardNumber?: string;
  nameOnCard?: string;
  cvv?: string;
  expirationDate?: string;
  isMasked?: boolean;
  badgeType?: 'in-handling' | 'not-available' | null;
  maxWidth?: number;
};

const CopyButton = ({ text }: { text: string }) => (
  <CopyToClipboard
    text={text}
    onCopy={() => showSuccessNotification({ message: 'Copied to clipboard' })}
  >
    <Box className={classes.copyIcon}>
      <IconCopy stroke={1} size={15} color="var(--color-text-on-primary)" />
    </Box>
  </CopyToClipboard>
);

const CardField = ({
  label,
  value,
  copyText,
}: {
  label: string;
  value: string;
  copyText: string;
}) => (
  <Stack gap={0}>
    <Text
      size="sm"
      c="var(--color-text-on-primary)"
      className={classes.fieldLabel}
    >
      {label}
    </Text>
    <Group gap="xxs" wrap="nowrap" className="fs-exclude">
      <Text size="md" c="var(--color-text-on-primary)">
        {value}
      </Text>
      <CopyButton text={copyText} />
    </Group>
  </Stack>
);

export const CreditCard = ({
  cardNumber,
  nameOnCard,
  cvv,
  expirationDate,
  isMasked,
  badgeType,
  maxWidth = DEFAULT_MAX_WIDTH,
}: Props) => {
  const hasDetails = Boolean(cardNumber);

  return (
    <Box
      className={!hasDetails ? classes.placeholderWrapper : undefined}
      style={{ maxWidth }}
    >
      {badgeType === 'in-handling' && (
        <Box className={classes.placeholderBadge}>
          <Pill variant="tag" bg="var(--brand-indigo-10)" bdrs="sm">
            <Text size="sm" c="var(--brand-indigo-800)">
              In-Handling
            </Text>
          </Pill>
        </Box>
      )}
      {badgeType === 'not-available' && (
        <Box className={classes.placeholderBadge}>
          <Pill variant="tag" bg="var(--brand-red-100)" bdrs="sm">
            <Text size="sm" c="var(--brand-red-500)">
              Not available
            </Text>
          </Pill>
        </Box>
      )}
      <Box
        className={`${classes.card} ${classes.cardDetails} ${!hasDetails ? classes.cardPlaceholder : ''}`}
        style={{ maxWidth }}
      >
        <CardChipIcon
          style={{
            position: 'absolute',
            top: '4.8rem',
            left: '1.4rem',
            width: '3rem',
            height: '2rem',
          }}
        />
        <Stack className={classes.content} justify="space-between">
          <LogoDark className={classes.logo} />
          <Stack gap={hasDetails ? 'xl' : 'xxl'}>
            {hasDetails && (
              <Group gap="xxl">
                <CardField
                  label="Card Holder"
                  value={nameOnCard ?? ''}
                  copyText={nameOnCard ?? ''}
                />
                <CardField
                  label="Expires"
                  value={expirationDate ?? ''}
                  copyText={expirationDate ?? ''}
                />
                <CardField
                  label="CVV"
                  value={getFormattedCVV(cvv ?? '', isMasked)}
                  copyText={cvv ?? ''}
                />
              </Group>
            )}
            <Group
              justify={hasDetails ? 'space-between' : 'flex-end'}
              wrap="nowrap"
            >
              {cardNumber && (
                <Group gap="xs" wrap="nowrap" className="fs-exclude">
                  <Text
                    size="md"
                    c="var(--color-text-on-primary)"
                    className={classes.cardNumber}
                  >
                    {getFormattedCardNumber(cardNumber, isMasked)}
                  </Text>
                  <CopyButton text={cardNumber} />
                </Group>
              )}
              <VisaLogo
                style={{
                  width: '4.5rem',
                  height: '1.5rem',
                  color: 'var(--color-text-on-primary)',
                }}
              />
            </Group>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
